import { useEffect, useState } from "react";
import type { Settings } from "@/shared/cache/slices/settings.types";
import {
	SettingGroup,
	SettingRow,
	StepInput,
	type NumericInput,
} from "../SettingsShared";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { Button } from "@/shared/components/ui/button";

const PROMPT_FIELD_MAX_CHARS = 2000;

type PromptKey =
	| "p_narrative_style_prompt"
	| "p_group_behavior_prompt"
	| "p_merge_prompt"
	| "p_collapse_prompt";

interface PromptBlockProps {
	title: string;
	description: string;
	placeholder: string;
	settingKey: PromptKey;
	settings: Settings;
	update: (patch: Partial<Settings>) => void;
}

// v1 uses debounced auto-save via `update()` (no promise / toast). The
// dirty-tracked Save/Cancel pair gives the user explicit control over when the
// long-form prompt is committed, same as v2.
function PromptBlock({
	title,
	description,
	placeholder,
	settingKey,
	settings,
	update,
}: PromptBlockProps) {
	const current = settings[settingKey];
	const [draft, setDraft] = useState(current);
	useEffect(() => {
		setDraft(current);
	}, [current]);
	const isDirty = draft !== current;
	const overLimit = draft.length > PROMPT_FIELD_MAX_CHARS;

	return (
		<div className="flex flex-col gap-3 py-5 border-b border-border/40">
			<div className="flex flex-col gap-1">
				<p className="text-sm font-medium font-sans text-foreground">{title}</p>
				<p className="text-xs text-muted-foreground leading-relaxed">
					{description}
				</p>
			</div>
			<Textarea
				value={draft}
				onChange={(e) =>
					setDraft(e.target.value.slice(0, PROMPT_FIELD_MAX_CHARS))
				}
				maxLength={PROMPT_FIELD_MAX_CHARS}
				placeholder={placeholder}
				className="min-h-[140px]"
			/>
			<div className="flex items-center justify-between">
				<span
					className={`text-xs ${
						overLimit ? "text-destructive" : "text-muted-foreground"
					}`}
				>
					{draft.length} / {PROMPT_FIELD_MAX_CHARS}
				</span>
				{isDirty && (
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setDraft(current)}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							disabled={overLimit}
							onClick={() => update({ [settingKey]: draft } as Partial<Settings>)}
						>
							Save
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export interface NarrativesSettingsProps {
	settings: Settings;
	update: (patch: Partial<Settings>) => void;
	wordCount: NumericInput;
}

export function NarrativesSettings({
	settings,
	update,
	wordCount,
}: NarrativesSettingsProps) {
	return (
		<>
			<SettingGroup label="Style">
				<SettingRow
					label="Paralegal mode"
					description="Adjust narrative style and formatting for paralegal billing"
				>
					<Switch
						checked={settings.paralegal_mode}
						onCheckedChange={(v) => update({ paralegal_mode: v })}
					/>
				</SettingRow>
				<SettingRow
					label="Draft detailed narratives"
					description="Generate longer, more detailed narrative descriptions for each entry"
				>
					<Switch
						checked={settings.insurance_defense_mode}
						onCheckedChange={(v) =>
							update({ insurance_defense_mode: v })
						}
					/>
				</SettingRow>
				{!settings.insurance_defense_mode && (
					<div className="pl-6">
						<SettingRow
							label="Check for restricted words"
							description="Flag narratives that contain words from your restricted words list"
						>
							<Switch
								checked={settings.check_for_red_words}
								onCheckedChange={(v) =>
									update({ check_for_red_words: v })
								}
							/>
						</SettingRow>
						<SettingRow
							label="Max word count"
							description="Maximum number of words allowed in a generated narrative"
						>
							<StepInput {...wordCount} />
						</SettingRow>
					</div>
				)}
				<PromptBlock
					title="Narrative style"
					description="General narrative style applied to every generated description (e.g. punctuation, phrasing, formatting). This is not for changing how Group or Merge blend entries."
					placeholder="e.g. End narratives with a period. Prefer 'review' over 'analyze'."
					settingKey="p_narrative_style_prompt"
					settings={settings}
					update={update}
				/>
			</SettingGroup>

			<SettingGroup label="Inclusion">
				{settings.p_track_internal_emails && (
					<SettingRow
						label="Include internal names in narratives"
						description="When tracking internal emails, allow names of internal participants to appear in the generated narrative. When off, internal names are scrubbed."
					>
						<Switch
							checked={settings.p_include_internal_in_narratives}
							onCheckedChange={(v) =>
								update({ p_include_internal_in_narratives: v })
							}
						/>
					</SettingRow>
				)}
				<SettingRow
					label="Add page numbers to narratives"
					description="Append page or slide counts to generated narratives (e.g. '5 pages')."
				>
					<Switch
						checked={settings.add_page_numbers}
						onCheckedChange={(v) => update({ add_page_numbers: v })}
					/>
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Group">
				<SettingRow
					label="Include units in grouped entries"
					description="Append each segment's units in parentheses, e.g. (0.1), when stacking entries with Group."
				>
					<Switch
						checked={settings.p_group_include_units}
						onCheckedChange={(v) =>
							update({ p_group_include_units: v })
						}
					/>
				</SettingRow>
				<PromptBlock
					title="Group instructions"
					description="Behavioral overrides applied when stacking entries via Group. Use this for instructions like always including the time of day when stacking emails."
					placeholder="e.g. Always include the time of day when stacking emails."
					settingKey="p_group_behavior_prompt"
					settings={settings}
					update={update}
				/>
			</SettingGroup>
		</>
	);
}
